import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import TranscriptionDetail from './pages/TranscriptionDetail';
import History from './pages/History';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import OpusConverterPage from './pages/OpusConverterPage';
import AudioConverterPage from './pages/AudioConverterPage';
import PublicSharePage from './pages/PublicSharePage';
import TrialTranscriptionDetail from './pages/TrialTranscriptionDetail';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import Security from './pages/Security';
import About from './pages/About';
import UploadPage from './pages/UploadPage';
import PhoneUploadPage from './pages/PhoneUploadPage';
import ApiDocs from './pages/ApiDocs';
import TranscribeAudioToText from './pages/TranscribeAudioToText';
import Layout from './components/Layout';
import { ThemeProvider } from './components/ThemeProvider';
import { LanguageProvider } from './components/LanguageProvider';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UploadManagerProvider } from './contexts/UploadManagerContext';
import UploadsTray from './components/UploadsTray';

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-zinc-950">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
  </div>
);

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Suspense fallback={<LoadingFallback />}>
        <ThemeProvider defaultTheme="system" storageKey="omniscribe-ui-theme">
          <LanguageProvider storageKey="omniscribe-language">
            <AuthProvider>
              <UploadManagerProvider>
                <BrowserRouter>
              <Routes>
                <Route path="/" element={<Layout variant="public"><Home /></Layout>} />
                <Route path="/transcribe-audio-to-text" element={<Layout variant="public"><TranscribeAudioToText /></Layout>} />
                <Route path="/pricing" element={<Layout variant="public"><Pricing /></Layout>} />
                <Route path="/api" element={<Layout variant="public"><ApiDocs /></Layout>} />
                <Route path="/tools/opus-converter" element={<Layout variant="public"><OpusConverterPage /></Layout>} />
                <Route path="/tools/audio-converter" element={<Layout variant="public"><AudioConverterPage /></Layout>} />
                <Route path="/terms" element={<Layout variant="public"><TermsOfService /></Layout>} />
                <Route path="/privacy" element={<Layout variant="public"><PrivacyPolicy /></Layout>} />
                <Route path="/refund-policy" element={<Layout variant="public"><RefundPolicy /></Layout>} />
                <Route path="/security" element={<Layout variant="public"><Security /></Layout>} />
                <Route path="/about" element={<Layout variant="public"><About /></Layout>} />
                <Route
                  path="/trial/transcription/:id"
                  element={<Layout variant="fullscreen"><TrialTranscriptionDetail /></Layout>}
                />

                {/* Auth Routes */}
                <Route path="/login" element={<Layout variant="public"><Login /></Layout>} />
                <Route path="/register" element={<Layout variant="public"><Register /></Layout>} />
                <Route path="/forgot-password" element={<Layout variant="public"><ForgotPassword /></Layout>} />
                <Route path="/auth/callback" element={<Layout variant="public"><AuthCallback /></Layout>} />

                {/* Public Share Route */}
                <Route path="/s/:token" element={<Layout variant="public"><PublicSharePage /></Layout>} />
                <Route path="/u/:token" element={<Layout variant="fullscreen"><PhoneUploadPage /></Layout>} />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout variant="app"><Dashboard /></Layout>
                  </ProtectedRoute>
                } />
                <Route path="/upload" element={
                  <ProtectedRoute>
                    <Layout variant="app"><UploadPage /></Layout>
                  </ProtectedRoute>
                } />
                <Route path="/files" element={
                  <ProtectedRoute>
                    <Navigate to="/history" replace />
                  </ProtectedRoute>
                } />
                <Route path="/history" element={
                  <ProtectedRoute>
                    <Layout variant="app"><History /></Layout>
                  </ProtectedRoute>
                } />
                <Route path="/transcription/:id" element={
                  <ProtectedRoute>
                    <Layout variant="fullscreen"><TranscriptionDetail /></Layout>
                  </ProtectedRoute>
                } />
                <Route path="/billing" element={
                  <ProtectedRoute>
                    <Layout variant="app"><Billing /></Layout>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Layout variant="app"><Settings /></Layout>
                  </ProtectedRoute>
                } />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <UploadsTray />
            </BrowserRouter>
          </UploadManagerProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  </Suspense>
</HelmetProvider>
  );
};

export default App;
